<template>
	<ClientOnly>
		<div class="booker-time-select">
			<v-menu
				v-if="!isMobile"
				v-model="isTimeSelectOpen"
				attach
				:close-on-content-click="false"
				content-class="booker-time-select__menu"
				style="z-index: 100">
				<div class="booker-time-select__container">
					<v-list class="booker-time-select__list">
						<v-list-item
							v-for="(time, i) in availableTimes"
							:key="i"
							link
							class="booker-time-select__single"
							:disabled="isTimeDisabled(time)"
							:class="{ 'booker-time-select__single--selected': selectedTime === time }"
							@click="selectTime(time)">
							<div ref="items" class="booker-time-select__value">{{ time }}</div>
							<div v-if="selectedTime === time" class="booker-time-select__check">
								<GlobalCheckbox v-model="isSelected" readonly></GlobalCheckbox>
							</div>
						</v-list-item>
					</v-list>
				</div>
			</v-menu>
			<GlobalModal v-else v-model="isTimeSelectOpen" noPadding class="booker-time-select__modal">
				<div class="booker-time-select__header">
					{{
						type === EBookerLocationType.START
							? $t('components.booker.date_select.pickup_time_label')
							: $t('components.booker.date_select.dropoff_time_label')
					}}
				</div>
				<div class="booker-time-select__container">
					<v-list class="booker-time-select__list">
						<v-list-item
							v-for="(time, i) in availableTimes"
							:key="i"
							link
							class="booker-time-select__single"
							:class="{ 'booker-time-select__single--selected': selectedTime === time }"
							@click="selectTime(time)">
							<div class="booker-time-select__value">{{ time }}</div>
							<div v-if="selectedTime === time" class="booker-time-select__check">
								<GlobalCheckbox v-model="isSelected" readonly></GlobalCheckbox>
							</div>
						</v-list-item>
					</v-list>
				</div>
			</GlobalModal>
		</div>
	</ClientOnly>
</template>

<script lang="ts" setup>
import { isToday, isBefore, format } from 'date-fns'
import { EBookerLocationType } from '@/data/bookerData'
const { isMobile } = useWindowWidth()

const bookerState = inject<Ref<IBookerState>>('bookerState')!
const { getStartDate } = useBookerService()
const isTimeSelectOpen = defineModel<boolean>()
const availableTimes = ref<string[]>([])
const selectedTime = ref<string | null>(null)
const isSelected = ref<boolean>(true)
const items = ref<any>()
const props = defineProps<{
	type?: EBookerLocationType
	initialTime?: any
	currentDate?: Date | number | string
}>()

const emit = defineEmits<{
	(e: 'selectTime', time: string, notUserAction?: boolean): void
}>()

selectedTime.value = props.type ? props.initialTime[props.type] : null

// const isTimeDisabled = (time: string) => {
// 	if (props.currentDate && !isToday(props.currentDate)) return false
// 	const [hours, minutes] = time.split(':').map(Number)
// 	const currentDate = new Date(props.currentDate)

// 	const currentHours = currentDate.getHours()
// 	const currentMinutes = currentDate.getMinutes()
// 	return hours < currentHours || (hours === currentHours && minutes < currentMinutes)
// }

const selectTime = (time: string, notUserAction?: boolean) => {
	isTimeSelectOpen.value = false
	selectedTime.value = time
	if (!notUserAction) {
		isSelected.value = true
	}
	emit('selectTime', time, notUserAction)
}

const isTimeDisabled = (time: string) => {
	if ((props.currentDate && !isToday(props.currentDate)) || props.type === EBookerLocationType.END) return false
	const [hours, minutes] = time.split(':').map(Number)
	const minDate = getStartDate()

	const minHours = minDate.getHours()
	const minMinutes = minDate.getMinutes()

	return hours < minHours || (hours === minHours && minutes < minMinutes)
}

const getAvailableTimes = () => {
	const times = []
	for (let i = 0; i < 24; i++) {
		times.push(`${i < 10 ? '0' : ''}${i}:00`)
		times.push(`${i < 10 ? '0' : ''}${i}:30`)
	}
	availableTimes.value = times
}
const scrollToTime = () => {
	if (!selectedTime.value || !items.value || (items.value && items.value.length === 0)) return
	const index = (parseInt(selectedTime.value, 10) - 1) * 2
	const item = items.value[index > 0 ? (index > 42 ? 47 : index + 6) : 0]
	if (item) item.scrollIntoView(false)
}

const setStartTimeToFirstPossible = () => {
	if (props.type !== EBookerLocationType.START) return

	const firstPossibleTime = getStartDate().getTime()
	const selectedTime = props.currentDate

	if (isBefore(new Date(selectedTime), new Date(firstPossibleTime))) {
		selectTime(format(new Date(firstPossibleTime), 'HH:mm'), true)
	}
}

watch(
	() => props.currentDate,
	() => {
		if (props.type === EBookerLocationType.START) {
			setStartTimeToFirstPossible()
		}
		getAvailableTimes()
	},
)

watch(
	() => bookerState.value.selectedDates,
	() => {
		if (props.type === EBookerLocationType.START) {
			setStartTimeToFirstPossible()
		}
		getAvailableTimes()
	},
	{ immediate: true, deep: true },
)

watch(
	() => items.value,
	() => {
		scrollToTime()
	},
	{ immediate: true },
)

watch(
	() => isTimeSelectOpen.value,
	async () => {
		if (isTimeSelectOpen.value) {
			await nextTick()
			scrollToTime()
		}
	},
	{ immediate: true },
)

getAvailableTimes()
</script>

<style lang="scss">
@import './DateSelect';
</style>
